// The Decision Support Template (DST) and the Decision Support Matrix (DSM) — MDMP (Military Decision
// Making Process) step 4's own two artifacts, built from the run instead of predicted before it.
//
// Doctrine (research 03 §1): the DST is "a combined intelligence and operations graphic based on the
// results of wargaming that depicts decision points, timelines associated with movement of forces, the
// flow of the operation"; the DSM is its written form, listing for each decision point its location,
// the criteria to evaluate, the options, and the unit responsible to observe and report. CALL's
// observation is that staffs produce the matrix in too little detail to be useful and never link the
// information requirements to the named areas of interest and the decision points — so every row here
// names the named area or phase line it came from, the observer who reported it, and the event id the
// replay scrubs to.
//
// The fourth column is the one a paper DSM cannot have. "BRANCH" re-runs the fight from that decision
// point with a different decision and shows the alternate outcome beside the real one, because the
// simulation is deterministic: everything before the branch tick is the same fight, so the difference
// is the decision (src/sim/branchRun.ts).

import { useEffect, useMemo, useRef, useState } from 'react';
import { branchDelta, type DecisionOption, type DecisionPoint } from '../../debrief/analyze';
import type { Grid } from '../../sim/grid';
import type { OrderLogEntry, TruthFrame } from '../../sim/sim';
import type { AoMeta, Event, Graphic, Scenario, Unit } from '../../sim/types';
import { DEMO_SEED, useStore } from '../../state/store';
import { useDebrief } from '../../state/debrief';
import { Chip } from '../cards/ui';
import { clockHM, gridRef } from '../reports';
import { Heading } from './Logs';
import { COLOR, drawGraphics, useDarkBasemap } from './Replay';
import { buildBranchOrders, useBranch, type BranchRunCtx } from './branchState';

const CANVAS_PX = 520;

export function DecisionSupport({
  dps, meta, grid, scenario, graphics, units, truth, events, orderLog,
}: {
  dps: DecisionPoint[];
  meta: AoMeta;
  grid: Grid | null;
  scenario: Scenario | null;
  graphics: Graphic[];
  units: Unit[];
  /** The real run's ground truth and log: what the branch is measured against. */
  truth: TruthFrame[];
  events: Event[];
  orderLog: OrderLogEntry[];
}) {
  const buffers = useStore((s) => s.buffers);
  const dials = useStore((s) => s.dials);
  const scrubToEvent = useDebrief((s) => s.scrubToEvent);
  const [selected, setSelected] = useState<string | null>(null);

  const openDpId = useBranch((s) => s.openDpId);
  const outcome = useBranch((s) => s.outcome);

  const runCtx: BranchRunCtx | null = useMemo(() => {
    if (!grid || !scenario || !buffers) return null;
    return { meta, buffers, scenario, grid, dials, seed: DEMO_SEED, units, truth, orderLog };
  }, [meta, buffers, scenario, grid, dials, units, truth, orderLog]);

  const select = (dp: DecisionPoint): void => {
    setSelected(dp.id);
    scrubToEvent(dp.t);
  };

  return (
    <section data-testid="decision-support" className="wt-panel p-3">
      <div className="flex items-baseline justify-between gap-2">
        <Heading>Decision support</Heading>
        <span className="mono text-[11px] text-fog" data-testid="dp-count">
          {dps.length} decision point{dps.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        The Decision Support Template (DST) and the Decision Support Matrix (DSM), the two artifacts
        step 4 of the MDMP (Military Decision Making Process) produces — except that a staff draws these before the fight
        as predictions, and these were taken out of the run afterwards as fact. A decision point is a
        moment the commander had to decide something to keep the attack synchronized. The criteria are
        only what was in <em>your</em> picture at that minute; nothing here is drawn from ground truth.
      </p>

      {dps.length === 0 ? (
        <p className="wt-empty mt-3">
          No decision point was reached: the force never tripped a named area of interest, crossed a
          phase line, lost a platoon or ran the tank dry.
        </p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[520px_1fr]">
            <Template
              dps={dps} meta={meta} grid={grid} graphics={graphics}
              selected={selected} onSelect={select}
            />
            <Legend dps={dps} grid={grid} selected={selected} onSelect={select} />
          </div>

          <Matrix
            dps={dps} grid={grid} runCtx={runCtx} openDpId={openDpId} outcome={outcome}
            truth={truth} events={events} units={units} onSelect={select}
          />
        </>
      )}
    </section>
  );
}

// ---- (a) the template graphic -----------------------------------------------------------------------

/** Where a decision point's marker goes: its own cell, else the control graphic it hangs off. */
function markerCm(dp: DecisionPoint, grid: Grid | null, graphics: Graphic[]): { x: number; y: number } | null {
  if (dp.cell !== undefined && grid) return grid.centerCm(dp.cell);
  const g = graphics.find((x) => x.id === dp.graphicId);
  if (!g || g.pts.length === 0) return null;
  const n = g.pts.length;
  return {
    x: g.pts.reduce((a, p) => a + p.x, 0) / n,
    y: g.pts.reduce((a, p) => a + p.y, 0) / n,
  };
}

function Template({
  dps, meta, grid, graphics, selected, onSelect,
}: {
  dps: DecisionPoint[];
  meta: AoMeta;
  grid: Grid | null;
  graphics: Graphic[];
  selected: string | null;
  onSelect: (dp: DecisionPoint) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const basemap = useDarkBasemap(meta.name);

  const placed = useMemo(
    () => dps
      .map((dp) => ({ dp, at: markerCm(dp, grid, graphics) }))
      .filter((p): p is { dp: DecisionPoint; at: { x: number; y: number } } => p.at !== null),
    [dps, grid, graphics],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const size = CANVAS_PX;
    const spanCm = meta.w * meta.cellM * 100;
    const px = (x: number): number => (x / spanCm) * size;
    const py = (y: number): number => size - (y / spanCm) * size;
    const mToPx = (m: number): number => ((m * 100) / spanCm) * size;

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = '#0b0f14';
    ctx.fillRect(0, 0, size, size);
    if (basemap) ctx.drawImage(basemap, 0, 0, size, size);
    // The same control measures the replay draws — the phase lines and named areas of interest are
    // the decision points' own criteria, so the template is unreadable without them.
    drawGraphics(ctx, graphics, px, py, mToPx);

    for (const { dp, at } of placed) {
      drawDpMarker(ctx, px(at.x), py(at.y), dp.n, dp.id === selected);
    }
  }, [basemap, graphics, meta, placed, selected]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={CANVAS_PX}
        height={CANVAS_PX}
        data-testid="dst-canvas"
        className="w-full rounded-sm border"
        style={{
          background: 'var(--color-ink)',
          aspectRatio: '1 / 1',
          borderColor: `color-mix(in srgb, ${COLOR.warn} 35%, var(--color-slate-700))`,
        }}
        onClick={(e) => {
          // Clicking the plate picks the nearest marker: the numbers are 9 px discs and the matrix
          // row is what the reader actually wants, so "near enough" is the right tolerance.
          const rect = e.currentTarget.getBoundingClientRect();
          const scale = CANVAS_PX / rect.width;
          const cx = (e.clientX - rect.left) * scale;
          const cy = (e.clientY - rect.top) * scale;
          const spanCm = meta.w * meta.cellM * 100;
          let best: { dp: DecisionPoint; d: number } | null = null;
          for (const { dp, at } of placed) {
            const dx = (at.x / spanCm) * CANVAS_PX - cx;
            const dy = CANVAS_PX - (at.y / spanCm) * CANVAS_PX - cy;
            const d = Math.hypot(dx, dy);
            if (!best || d < best.d) best = { dp, d };
          }
          if (best && best.d < 24) onSelect(best.dp);
        }}
      />
      <p className="mt-1 text-[11px] leading-snug text-fog">
        The Decision Support Template: each numbered marker is a decision point, on the ground where it
        was raised, over the phase lines and named areas of interest it was raised against. Click a
        marker, or a number in the matrix, to scrub the replay to that minute.
      </p>
    </div>
  );
}

/** A numbered decision-point disc: amber by default, friendly cyan and ringed when it is the selected one. */
function drawDpMarker(ctx: CanvasRenderingContext2D, x: number, y: number, n: number, active: boolean): void {
  const color = active ? COLOR.friendly : COLOR.warn;
  const r = active ? 11 : 9;
  if (active) {
    ctx.beginPath();
    ctx.arc(x, y, r + 5, 0, Math.PI * 2);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.6;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = '#0b0f14';
  ctx.globalAlpha = 0.85;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = color;
  ctx.stroke();

  ctx.fillStyle = color;
  ctx.font = 'bold 10px "IBM Plex Mono", ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), x, y + 0.5);
  ctx.textBaseline = 'alphabetic';
}

function Legend({
  dps, grid, selected, onSelect,
}: {
  dps: DecisionPoint[];
  grid: Grid | null;
  selected: string | null;
  onSelect: (dp: DecisionPoint) => void;
}) {
  return (
    <ul className="thin-scroll max-h-[520px] space-y-1 overflow-auto pr-1">
      {dps.map((dp) => (
        <li key={dp.id}>
          <button
            type="button"
            data-testid="dp-legend-item"
            onClick={() => onSelect(dp)}
            className="wt-card block w-full text-left"
            style={{
              borderLeftWidth: 2,
              borderLeftColor: dp.id === selected ? COLOR.friendly : COLOR.warn,
            }}
          >
            <span className="mono text-[11px] text-paper">{dp.title}</span>
            <span className="mono ml-2 text-[10px] text-fog">
              {clockHM(dp.t)} · {dp.locationLabel}
              {dp.cell !== undefined ? ` · grid ${gridRef(dp.cell, grid)}` : ''}
            </span>
            <span className="mono mt-0.5 block text-[10px] leading-snug text-fog">
              Observed by {dp.observer?.callsign ?? 'the staff'} · you {dp.chosen.summary === 'no order' ? 'gave no order' : `ordered ${dp.chosen.summary}`}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---- (b) the matrix -------------------------------------------------------------------------------

function Matrix({
  dps, grid, runCtx, openDpId, outcome, truth, events, units, onSelect,
}: {
  dps: DecisionPoint[];
  grid: Grid | null;
  runCtx: BranchRunCtx | null;
  openDpId: string | null;
  outcome: ReturnType<typeof useBranch.getState>['outcome'];
  truth: TruthFrame[];
  events: Event[];
  units: Unit[];
  onSelect: (dp: DecisionPoint) => void;
}) {
  const toggleOpen = useBranch((s) => s.toggleOpen);

  return (
    <div className="thin-scroll mt-3 overflow-auto">
      <table className="mono w-full border-collapse text-[11px]">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-[0.14em] text-fog">
            <th className="px-1.5 py-1 font-normal">DP</th>
            <th className="px-1.5 py-1 font-normal">Time</th>
            <th className="px-1.5 py-1 font-normal">Location</th>
            <th className="px-1.5 py-1 font-normal">Criteria</th>
            <th className="px-1.5 py-1 font-normal">Options</th>
            <th className="px-1.5 py-1 font-normal">What you chose</th>
            <th className="px-1.5 py-1 font-normal">Outcome</th>
            <th className="px-1.5 py-1 font-normal">Who observed</th>
            <th className="px-1.5 py-1 font-normal" />
          </tr>
        </thead>
        <tbody>
          {dps.map((dp) => (
            <DsmRow
              key={dp.id}
              dp={dp} grid={grid} runCtx={runCtx}
              open={openDpId === dp.id}
              outcome={outcome && outcome.dpId === dp.id ? outcome : null}
              truth={truth} events={events} units={units}
              onSelect={onSelect} onToggle={() => toggleOpen(dp.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DsmRow({
  dp, grid, runCtx, open, outcome, truth, events, units, onSelect, onToggle,
}: {
  dp: DecisionPoint;
  grid: Grid | null;
  runCtx: BranchRunCtx | null;
  open: boolean;
  outcome: ReturnType<typeof useBranch.getState>['outcome'];
  truth: TruthFrame[];
  events: Event[];
  units: Unit[];
  onSelect: (dp: DecisionPoint) => void;
  onToggle: () => void;
}) {
  return (
    <>
      <tr data-testid="dsm-row" data-dp={dp.n} className="border-t border-slate-800 align-top">
        <td className="px-1.5 py-1">
          <button
            type="button"
            data-testid="dp-link"
            onClick={() => onSelect(dp)}
            title="Scrub the replay to this minute"
            className="mono text-[12px]"
            style={{ color: COLOR.warn }}
          >
            DP {dp.n}
          </button>
        </td>
        <td className="whitespace-nowrap px-1.5 py-1 text-fog">{clockHM(dp.t)}</td>
        <td className="px-1.5 py-1 text-paper">
          {dp.locationLabel}
          {dp.cell !== undefined && (
            <span className="block text-[10px] text-fog">grid {gridRef(dp.cell, grid)}</span>
          )}
        </td>
        <td className="max-w-[280px] px-1.5 py-1 leading-snug text-fog">
          <ul className="list-disc space-y-0.5 pl-3">
            {dp.criteria.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </td>
        <td className="max-w-[200px] px-1.5 py-1 leading-snug text-fog">
          <ul className="space-y-0.5">
            {dp.options.map((o) => (
              <li key={o.id} title={o.detail}>
                {o.source === 'play' ? '▸ ' : '· '}{o.label}
              </li>
            ))}
          </ul>
        </td>
        <td className="max-w-[180px] px-1.5 py-1 leading-snug" style={{ color: dp.chosen.orders.length === 0 ? 'var(--color-fog)' : 'var(--color-paper)' }}>
          {dp.chosen.summary}
        </td>
        <td className="max-w-[220px] px-1.5 py-1 leading-snug text-fog">{dp.outcome.summary}</td>
        <td className="whitespace-nowrap px-1.5 py-1 text-fog">{dp.observer?.callsign ?? 'the staff'}</td>
        <td className="px-1.5 py-1">
          <Chip
            tone="warn"
            active={open}
            testId="branch-chip"
            onClick={onToggle}
            title="Re-run the fight from this minute with a different decision"
          >
            branch
          </Chip>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-slate-800">
          <td colSpan={9} className="px-1.5 pb-3 pt-1">
            <BranchPanel dp={dp} runCtx={runCtx} outcome={outcome} truth={truth} events={events} units={units} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---- (c) the branch panel --------------------------------------------------------------------------

function BranchPanel({
  dp, runCtx, outcome, truth, events, units,
}: {
  dp: DecisionPoint;
  runCtx: BranchRunCtx | null;
  outcome: ReturnType<typeof useBranch.getState>['outcome'];
  truth: TruthFrame[];
  events: Event[];
  units: Unit[];
}) {
  const selectedOptionId = useBranch((s) => s.selectedOptionId);
  const selectOption = useBranch((s) => s.selectOption);
  const running = useBranch((s) => s.running);
  const progress = useBranch((s) => s.progress);
  const error = useBranch((s) => s.error);
  const run = useBranch((s) => s.run);

  const option = dp.options.find((o) => String(o.id) === selectedOptionId) ?? null;
  const built = useMemo(
    () => (option && runCtx ? buildBranchOrders(option, dp, runCtx) : null),
    [option, dp, runCtx],
  );

  const delta = useMemo(
    () => (outcome ? branchDelta({ truth, events }, { truth: outcome.result.truth, events: outcome.result.events }, units) : null),
    [outcome, truth, events, units],
  );

  const blocked = !runCtx
    ? 'The area of operations is no longer loaded, so the fight cannot be re-run.'
    : !option
      ? 'Pick one alternative.'
      : built && built.orders.length === 0
        ? built.reason
        : null;

  return (
    <div data-testid="branch-panel" className="wt-card" style={{ borderLeftWidth: 2, borderLeftColor: COLOR.warn }}>
      <p className="text-[11px] leading-snug text-fog">
        Re-runs the fight from <span className="text-paper">{clockHM(dp.t)}</span> with one decision
        changed: your orders in the five minutes after that minute are dropped and the alternative is
        issued instead. Everything before it is the run you actually fought, tick for tick — the enemy
        is not re-rolled — so the difference between the two outcomes is the decision.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-1">
        {dp.options.map((o) => (
          <BranchOptionChip
            key={o.id}
            option={o}
            active={String(o.id) === selectedOptionId}
            onClick={() => selectOption(String(o.id))}
          />
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="branch-run"
          disabled={running || blocked !== null}
          onClick={() => { if (option && runCtx) void run(dp, option, runCtx); }}
          title={blocked ?? 'Fly this alternative to the end of the scenario'}
          className="mono rounded-sm border px-2 py-1 text-[11px] uppercase tracking-[0.18em]"
          style={{
            borderColor: running || blocked ? 'var(--color-slate-700)' : COLOR.warn,
            color: running || blocked ? 'var(--color-fog)' : COLOR.warn,
            background: running || blocked ? 'transparent' : `color-mix(in srgb, ${COLOR.warn} 12%, transparent)`,
            cursor: running || blocked ? 'default' : 'pointer',
          }}
        >
          {running ? 'running…' : 'Run this branch'}
        </button>
        {blocked && !running && (
          <span data-testid="branch-blocked" className="mono text-[10px] text-fog">{blocked}</span>
        )}
        {built && built.orders.length > 0 && !running && (
          <span className="mono text-[10px] text-fog">
            {built.orders.length} order{built.orders.length === 1 ? '' : 's'} at {clockHM(dp.t)}
          </span>
        )}
      </div>

      {running && (
        <div data-testid="branch-progress" className="mt-2">
          <div className="flex items-baseline justify-between">
            <span className="mono text-[10px] text-fog">re-running the fight</span>
            <span className="mono text-[10px] text-paper">{clockHM(progress.tick)} / {clockHM(progress.untilTick)}</span>
          </div>
          <span className="mt-1 block h-[3px] rounded-full" style={{ background: 'var(--color-slate-700)' }}>
            <span
              className="block h-full rounded-full"
              style={{
                width: `${progress.untilTick > 0 ? Math.min(100, (progress.tick / progress.untilTick) * 100) : 0}%`,
                background: COLOR.warn,
                transition: 'width 120ms linear',
              }}
            />
          </span>
        </div>
      )}

      {error && <p data-testid="branch-error" className="mono mt-2 text-[10px] text-hostile">{error}</p>}

      {outcome && delta && (
        <div className="mt-2 space-y-1">
          <div className="mono text-[9px] uppercase tracking-[0.14em] text-fog">
            "{outcome.optionLabel}" instead — what would have happened
          </div>
          <p data-testid="branch-delta" className="text-[13px] leading-snug" style={{ color: COLOR.warn }}>
            {delta.line}.
          </p>
          <p className="mono text-[10px] leading-snug text-fog">
            Real run: {outcomeWord(events)} at {clockHM(lastTick(truth))}, {delta.platoonsIntactReal} maneuver
            platoons intact, {delta.redDestroyedReal} enemy destroyed. Branch:{' '}
            {outcomeWord(outcome.result.events, outcome.result.ended)} at {clockHM(outcome.result.tick)},{' '}
            {delta.platoonsIntactAlt} intact, {delta.redDestroyedAlt} enemy destroyed.
          </p>
          <p className="mono text-[10px] leading-snug text-fog">
            The right-hand replay pane above is now the branch. Scrub it against the left pane, which is
            still the picture you actually had.
          </p>
        </div>
      )}
    </div>
  );
}

function BranchOptionChip({
  option, active, onClick,
}: {
  option: DecisionOption;
  active: boolean;
  onClick: () => void;
}) {
  const isPlay = option.source === 'play';
  return (
    <Chip
      tone={isPlay ? 'neutral' : 'friendly'}
      active={active}
      testId="branch-option"
      onClick={onClick}
      title={isPlay ? `${option.detail} Branching it would replay what happened.` : option.detail}
    >
      {option.label}
    </Chip>
  );
}

function lastTick(truth: TruthFrame[]): number {
  return truth.length > 0 ? truth[truth.length - 1].tick : 0;
}

/** "objective seized" / "no seizure" from a run's own events, or from its recorded end condition. */
function outcomeWord(events: Event[], ended?: 'seized' | 'culminated' | 'time' | null): string {
  if (events.some((e) => e.kind === 'obj.seized')) return 'objective seized';
  if (ended === 'culminated') return 'culminated';
  if (ended === 'time') return 'time expired';
  return 'no seizure';
}
